/**
 * Product zoom modal: full-screen overlay with zoom, pan, pinch, prev/next.
 * Matches Next.js ProductZoomModal behaviour. Responsive.
 */
(function () {
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_STEP = 0.5;
  const ZOOM_FULL_SIZE = 2;
  const CLICK_MOVE_THRESHOLD_PX = 8;
  const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function getFocusables(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (el) {
      return !el.hasAttribute('disabled') && el.offsetParent !== null;
    });
  }

  function initZoomModal(section) {
    const modal = section.querySelector('[data-product-zoom-modal]');
    const scriptEl = section.querySelector('[data-zoom-images]');
    const openBtn = section.querySelector('[data-product-zoom-open]');
    const gallery = section.querySelector('[data-product-gallery]');
    if (!modal || !scriptEl || !openBtn) return;

    let zoomImages = [];
    try {
      zoomImages = JSON.parse(scriptEl.textContent || '[]');
    } catch (e) {
      return;
    }
    if (zoomImages.length === 0) return;

    const zoomArea = modal.querySelector('[data-zoom-wheel-target]');
    const imageWrap = modal.querySelector('.product-zoom-modal__image-wrap');
    const img = modal.querySelector('[data-zoom-image]');
    const counterEl = modal.querySelector('[data-zoom-counter]');
    const pctEl = modal.querySelector('[data-zoom-pct]');
    const btnPrev = modal.querySelector('[data-zoom-prev]');
    const btnNext = modal.querySelector('[data-zoom-next]');
    const btnClose = modal.querySelector('[data-zoom-close]');
    const btnZoomIn = modal.querySelector('[data-zoom-in]');
    const btnZoomOut = modal.querySelector('[data-zoom-out]');

    let scale = ZOOM_MIN;
    let translate = { x: 0, y: 0 };
    let currentZoomIndex = 0;
    let isPanning = false;
    let previousActiveElement = null;
    let dragRef = { isActive: false, startX: 0, startY: 0, startTranslateX: 0, startTranslateY: 0 };
    let dragIntentRef = null;
    let pinchRef = null;
    let pointersRef = new Map();
    let clickStartRef = null;

    function getMediaIndex() {
      return gallery && gallery._productGalleryCurrentIndex != null ? gallery._productGalleryCurrentIndex : 0;
    }

    function mediaIndexToZoomIndex(mediaIndex) {
      for (var i = 0; i < zoomImages.length; i++) {
        if (zoomImages[i].mediaIndex === mediaIndex) return i;
      }
      return 0;
    }

    function setZoomIndex(index) {
      currentZoomIndex = ((index % zoomImages.length) + zoomImages.length) % zoomImages.length;
      var item = zoomImages[currentZoomIndex];
      img.src = item.src;
      img.alt = item.alt || '';
      if (counterEl) counterEl.textContent = zoomImages.length > 1 ? (currentZoomIndex + 1) + ' / ' + zoomImages.length : '';
      if (gallery && typeof gallery._productGallerySetActive === 'function') {
        gallery._productGallerySetActive(item.mediaIndex);
      }
    }

    function resetZoom() {
      scale = ZOOM_MIN;
      translate = { x: 0, y: 0 };
      applyTransform();
      updatePct();
      updateZoomButtons();
    }

    function applyTransform() {
      if (!imageWrap) return;
      imageWrap.style.transform = 'scale(' + scale + ') translate(' + translate.x + 'px, ' + translate.y + 'px)';
      imageWrap.style.cursor = scale <= ZOOM_MIN ? 'zoom-in' : isPanning ? 'grabbing' : 'grab';
      if (zoomArea) zoomArea.style.cursor = imageWrap.style.cursor;
    }

    function updatePct() {
      if (pctEl) pctEl.textContent = Math.round(scale * 100) + '%';
    }

    function updateZoomButtons() {
      if (btnZoomIn) btnZoomIn.disabled = scale >= ZOOM_MAX;
      if (btnZoomOut) btnZoomOut.disabled = scale <= ZOOM_MIN;
    }

    function zoomIn() {
      scale = Math.min(ZOOM_MAX, scale + ZOOM_STEP);
      applyTransform();
      updatePct();
      updateZoomButtons();
    }

    function zoomOut() {
      scale = Math.max(ZOOM_MIN, scale - ZOOM_STEP);
      if (scale === ZOOM_MIN) translate = { x: 0, y: 0 };
      applyTransform();
      updatePct();
      updateZoomButtons();
    }

    function toggleClickZoom() {
      if (scale <= ZOOM_MIN) scale = ZOOM_FULL_SIZE;
      else {
        scale = ZOOM_MIN;
        translate = { x: 0, y: 0 };
      }
      applyTransform();
      updatePct();
      updateZoomButtons();
    }

    function updateNavButtons() {
      if (btnPrev) btnPrev.disabled = zoomImages.length <= 1;
      if (btnNext) btnNext.disabled = zoomImages.length <= 1;
    }

    function openModal() {
      var mediaIdx = getMediaIndex();
      currentZoomIndex = mediaIndexToZoomIndex(mediaIdx);
      setZoomIndex(currentZoomIndex);
      resetZoom();
      updateNavButtons();
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('product-zoom-modal--open');
      previousActiveElement = document.activeElement;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.inset = '0';
      document.body.style.width = '100%';
      var firstFocus = modal.querySelector(FOCUSABLE_SELECTOR);
      if (firstFocus) firstFocus.focus();
    }

    function closeModal() {
      modal.setAttribute('aria-hidden', 'true');
      modal.classList.remove('product-zoom-modal--open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.inset = '';
      document.body.style.width = '';
      if (previousActiveElement && typeof previousActiveElement.focus === 'function') previousActiveElement.focus();
    }

    function goPrev() {
      if (zoomImages.length <= 1) return;
      setZoomIndex(currentZoomIndex - 1);
      resetZoom();
    }

    function goNext() {
      if (zoomImages.length <= 1) return;
      setZoomIndex(currentZoomIndex + 1);
      resetZoom();
    }

    function getPointersDistance() {
      var entries = Array.from(pointersRef.values());
      if (entries.length < 2) return 0;
      var a = entries[0], b = entries[1];
      return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) || 1;
    }

    function handlePointerDown(e) {
      pointersRef.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
      if (pointersRef.size === 1 && e.pointerType === 'mouse') {
        clickStartRef = { x: e.clientX, y: e.clientY };
      }
      if (pointersRef.size === 2) {
        clickStartRef = null;
        dragIntentRef = null;
        pinchRef = { distance: getPointersDistance(), scale: scale };
        dragRef.isActive = false;
        isPanning = false;
      } else if (pointersRef.size === 1 && scale > ZOOM_MIN) {
        dragIntentRef = { startX: e.clientX, startY: e.clientY };
        dragRef = { isActive: false, startX: e.clientX, startY: e.clientY, startTranslateX: translate.x, startTranslateY: translate.y };
        if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
      }
    }

    function handlePointerMove(e) {
      pointersRef.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
      if (pinchRef && pointersRef.size === 2) {
        var dist = getPointersDistance();
        scale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, (pinchRef.scale * dist) / pinchRef.distance));
        if (scale === ZOOM_MIN) translate = { x: 0, y: 0 };
        applyTransform();
        updatePct();
        updateZoomButtons();
        return;
      }
      var intent = dragIntentRef;
      if (intent && pointersRef.size === 1 && !dragRef.isActive) {
        var dx = e.clientX - intent.startX, dy = e.clientY - intent.startY;
        if (Math.hypot(dx, dy) >= CLICK_MOVE_THRESHOLD_PX) {
          dragRef.isActive = true;
          isPanning = true;
          dragIntentRef = null;
        }
      }
      if (dragRef.isActive) {
        translate = {
          x: dragRef.startTranslateX + e.clientX - dragRef.startX,
          y: dragRef.startTranslateY + e.clientY - dragRef.startY
        };
        applyTransform();
      }
    }

    function handlePointerUp(e) {
      var wasSingle = pointersRef.size === 1;
      var start = clickStartRef;
      var dx = start ? e.clientX - start.x : 0, dy = start ? e.clientY - start.y : 0;
      var movedLittle = start && Math.hypot(dx, dy) < CLICK_MOVE_THRESHOLD_PX;
      var isDesktopClick = e.pointerType === 'mouse' && wasSingle && movedLittle && !dragRef.isActive;

      pointersRef.delete(e.pointerId);
      if (pointersRef.size < 2) pinchRef = null;
      if (pointersRef.size === 0) {
        clickStartRef = null;
        dragIntentRef = null;
        dragRef.isActive = false;
        isPanning = false;
        if (isDesktopClick) toggleClickZoom();
      }
      if (e.target.releasePointerCapture) e.target.releasePointerCapture(e.pointerId);
    }

    function handleWheel(e) {
      e.preventDefault();
      if (e.deltaY < 0) zoomIn();
      else zoomOut();
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
        return;
      }
      if (e.key === 'Tab' && modal) {
        var focusables = getFocusables(modal);
        if (focusables.length === 0) return;
        var first = focusables[0], last = focusables[focusables.length - 1], active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    openBtn.addEventListener('click', function () {
      openModal();
    });

    btnClose.addEventListener('click', closeModal);
    btnPrev.addEventListener('click', goPrev);
    btnNext.addEventListener('click', goNext);
    btnZoomIn.addEventListener('click', zoomIn);
    btnZoomOut.addEventListener('click', zoomOut);

    if (zoomArea) {
      zoomArea.addEventListener('pointerdown', handlePointerDown);
      zoomArea.addEventListener('pointermove', handlePointerMove);
      zoomArea.addEventListener('pointerup', handlePointerUp);
      zoomArea.addEventListener('pointerleave', handlePointerUp);
      zoomArea.addEventListener('pointercancel', handlePointerUp);
      zoomArea.addEventListener('touchmove', function (e) { e.preventDefault(); }, { passive: false });
    }

    modal.addEventListener('keydown', handleKeyDown);

    var wheelTarget = zoomArea || modal;
    wheelTarget.addEventListener('wheel', handleWheel, { passive: false });
  }

  function run() {
    var sections = document.querySelectorAll('.section-main-product');
    sections.forEach(function (section) {
      if (section.querySelector('[data-product-zoom-modal]') && section.querySelector('[data-zoom-images]')) {
        initZoomModal(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var sectionId = e.detail && e.detail.sectionId;
    if (!sectionId) return;
    var section = document.getElementById('product-' + sectionId);
    if (section && section.querySelector('[data-product-zoom-modal]')) {
      initZoomModal(section);
    }
  });
})();
